import { jwtDecode } from "jwt-decode";
import { DecodedToken } from "src/app/interfaces";
import { persistedKeys, UserState } from "src/app/store/user/user.state";
import { environment } from "src/environments/environment";
import { getSelectedProgramId } from "./selected-program-id";

export function getUserLocalStorageKey(programId: string): string {
  return `${programId}_selfservice_${environment.name}_user`;
}

function parseUserStorage(raw: string | null): Partial<UserState> | undefined {
  if (!raw) return undefined;

  try {
    return JSON.parse(raw) as Partial<UserState>;
  } catch {
    return undefined;
  }
}

function organizationFromAccessToken(accessToken: string): string | undefined {
  try {
    return jwtDecode<DecodedToken>(accessToken).organization?.trim().toLowerCase();
  } catch {
    return undefined;
  }
}

export function pickPersistedUserState(state: UserState): Partial<UserState> {
  return Object.fromEntries(persistedKeys.map(key => [key, state[key as keyof UserState]]));
}

export function writeUserStateToStorage(programId: string, state: Partial<UserState>): void {
  localStorage.setItem(getUserLocalStorageKey(programId), JSON.stringify(state));
}

/** Finds a persisted session whose JWT organization matches the given program id. */
export function findUserAuthForOrganization(organizationProgramId: string): Partial<UserState> | undefined {
  const targetOrg = organizationProgramId.trim().toLowerCase();
  if (!targetOrg) return undefined;

  const suffix = `_selfservice_${environment.name}_user`;

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key?.endsWith(suffix)) continue;

    const stored = parseUserStorage(localStorage.getItem(key));
    if (!stored?.accessToken) continue;

    if (organizationFromAccessToken(stored.accessToken) === targetOrg) {
      return stored;
    }
  }

  return undefined;
}

/**
 * Ensures the current program's localStorage bucket contains the auth session for that org.
 * After switching organizations, the token is often saved under the previous org's storage key.
 */
export function hydrateUserAuthForCurrentProgram(): Partial<UserState> | undefined {
  const programId = getSelectedProgramId();
  if (!programId) return undefined;

  const current = parseUserStorage(localStorage.getItem(getUserLocalStorageKey(programId)));
  if (current?.accessToken) return current;

  const migrated = findUserAuthForOrganization(programId);
  if (migrated) {
    writeUserStateToStorage(programId, migrated);
  }

  return migrated;
}
